import { Extension, type CommandProps, type Editor } from '@tiptap/core';
import {
  ESTILO_PADRAO,
  PREFIXO_PERSONALIZADO,
  diferencaDeFormato,
  estiloPersonalizado,
  formatoDoEstilo,
  formatoDoPronto,
  formatoEfetivo,
  formatoSeguro,
  pilhaDaFonte,
  type FormatoParagrafo,
} from '../estilos';

/**
 * FORMATO DE PARÁGRAFO — estilo pronto + ajustes, num atributo cada.
 *
 * Sai no HTML de dois jeitos ao mesmo tempo:
 *   - `style="…"` com cm e pt, para que PDF, e-mail e Word recebam a peça
 *     como foi escrita, sem depender de CSS nenhum;
 *   - `data-estilo` / `data-ajustes`, para o editor reler o que ele mesmo
 *     gravou.
 *
 * Só os `data-*` são LIDOS de volta. É isso que torna a colagem do Word
 * segura: o `<p style="margin:0 0 12pt;line-height:115%">` que vem colado não
 * traz `data-*`, então o espaçamento alheio não entra e o parágrafo assume o
 * estilo de onde caiu (ver `colagemSegura`). Era o defeito que o editor do
 * AGIL resolvia apagando estilo do DOM a cada edição.
 */

type Ajustes = Partial<FormatoParagrafo>;

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    formatoParagrafo: {
      /** Aplica um estilo (pronto ou do escritório, `u:<id>`) e descarta os ajustes. */
      definirEstilo: (nome: string) => ReturnType;
      /** Redesenha todo parágrafo: os estilos do escritório mudaram. */
      redesenharEstilos: () => ReturnType;
      /** Ajusta campos do formato por cima do estilo. */
      ajustarParagrafo: (ajustes: Ajustes) => ReturnType;
      /** Ajusta TODOS os parágrafos de um estilo na peça (a formatação de um arquivo, só nesta peça). */
      ajustarEstiloNoTexto: (estilo: string, ajustes: Ajustes) => ReturnType;
      /** Volta ao Corpo da peça, sem ajustes e sem marcas de texto. */
      limparFormatacao: () => ReturnType;
    };
  }
}

const TIPOS = ['paragraph'];

export function cssDoFormato(f: FormatoParagrafo): string {
  const partes = [
    `font-family:${pilhaDaFonte(f.fonte)}`,
    `font-size:${f.tamanho}pt`,
    `text-align:${f.alinhamento}`,
    `line-height:${f.entrelinha}`,
    `margin:${f.espacoAntes}pt ${f.recuoDireito}cm ${f.espacoDepois}pt ${f.recuoEsquerdo}cm`,
  ];
  if (f.especial === 'primeira') partes.push(`text-indent:${f.medida}cm`);
  else if (f.especial === 'deslocado') partes.push(`text-indent:-${f.medida}cm`, `padding-left:${f.medida}cm`);
  if (f.negrito) partes.push('font-weight:700');
  if (f.italico) partes.push('font-style:italic');
  return partes.join(';');
}

function lerAjustes(valor: string | null): Ajustes | null {
  if (!valor) return null;
  try {
    // Validado campo a campo: o valor vai para o `style` (ver `formatoSeguro`).
    return formatoSeguro(JSON.parse(valor));
  } catch {
    return null;
  }
}

export const FormatoParagrafoExt = Extension.create({
  name: 'formatoParagrafo',

  addGlobalAttributes() {
    return [
      {
        types: TIPOS,
        attributes: {
          estilo: {
            default: null as string | null,
            parseHTML: (el) => el.getAttribute('data-estilo'),
            // O `style` sai daqui porque é aqui que se tem os dois atributos:
            // o renderHTML de um atributo recebe todos os do nó.
            renderHTML: (attrs) => {
              const f = formatoEfetivo(attrs.estilo, attrs.ajustes, attrs.estiloBase);
              const saida: Record<string, string> = { 'data-estilo': attrs.estilo ?? ESTILO_PADRAO, style: cssDoFormato(f) };
              // Estilo do escritório leva consigo a forma que tinha: quem abrir a
              // peça sem esse estilo (outra conta, outro app) vê a peça igual.
              if (typeof attrs.estilo === 'string' && attrs.estilo.startsWith(PREFIXO_PERSONALIZADO)) {
                const base = estiloPersonalizado(attrs.estilo)
                  ? diferencaDeFormato(formatoDoPronto(ESTILO_PADRAO, false), formatoDoEstilo(attrs.estilo))
                  : attrs.estiloBase;
                if (base && Object.keys(base).length) saida['data-estilo-base'] = JSON.stringify(base);
              }
              return saida;
            },
          },
          estiloBase: {
            default: null as Ajustes | null,
            parseHTML: (el) => lerAjustes(el.getAttribute('data-estilo-base')),
            rendered: false,
          },
          // Não vai ao HTML: mudar de valor obriga o ProseMirror a redesenhar o
          // parágrafo quando um estilo do escritório muda (os atributos dele não).
          rev: {
            default: 0,
            rendered: false,
          },
          ajustes: {
            default: null as Ajustes | null,
            parseHTML: (el) => lerAjustes(el.getAttribute('data-ajustes')),
            renderHTML: (attrs) =>
              attrs.ajustes && Object.keys(attrs.ajustes).length
                ? { 'data-ajustes': JSON.stringify(attrs.ajustes) }
                : {},
          },
        },
      },
    ];
  },

  addCommands() {
    const cadaParagrafo = (fn: (attrs: Record<string, unknown>) => Record<string, unknown>) =>
      ({ tr, state, dispatch }: CommandProps): boolean => {
        const { from, to } = state.selection;
        let mudou = false;
        state.doc.nodesBetween(from, to, (node, pos) => {
          if (!TIPOS.includes(node.type.name)) return;
          tr.setNodeMarkup(pos, undefined, { ...node.attrs, ...fn(node.attrs) });
          mudou = true;
        });
        if (mudou && dispatch) dispatch(tr);
        return mudou;
      };
    // Guarda só o que difere do estilo: ajuste igual ao estilo não é ajuste.
    const comAjustes = (attrs: Record<string, unknown>, ajustes: Ajustes) => {
      const base = formatoEfetivo(attrs.estilo as string, null, attrs.estiloBase as Ajustes | null);
      const atuais = { ...((attrs.ajustes as Ajustes) ?? {}), ...ajustes };
      const limpos: Ajustes = {};
      (Object.keys(atuais) as (keyof FormatoParagrafo)[]).forEach((k) => {
        if (atuais[k] !== base[k]) (limpos as Record<string, unknown>)[k] = atuais[k];
      });
      return { ajustes: Object.keys(limpos).length ? limpos : null };
    };

    return {
      definirEstilo: (nome) => cadaParagrafo(() => ({ estilo: nome, ajustes: null, estiloBase: null })),
      redesenharEstilos: () => ({ tr, state, dispatch }) => {
        state.doc.descendants((node, pos) => {
          if (TIPOS.includes(node.type.name)) tr.setNodeMarkup(pos, undefined, { ...node.attrs, rev: ((node.attrs.rev as number) ?? 0) + 1 });
        });
        tr.setMeta('addToHistory', false);
        if (dispatch) dispatch(tr);
        return true;
      },
      ajustarParagrafo: (ajustes) => cadaParagrafo((attrs) => comAjustes(attrs, ajustes)),
      ajustarEstiloNoTexto: (estilo, ajustes) => ({ tr, state, dispatch }) => {
        let mudou = false;
        state.doc.descendants((node, pos) => {
          if (!TIPOS.includes(node.type.name) || ((node.attrs.estilo as string | null) ?? ESTILO_PADRAO) !== estilo) return;
          tr.setNodeMarkup(pos, undefined, { ...node.attrs, ...comAjustes(node.attrs, ajustes) });
          mudou = true;
        });
        if (mudou && dispatch) dispatch(tr);
        return mudou;
      },
      limparFormatacao: () => ({ chain }) =>
        chain()
          .unsetAllMarks()
          .command(cadaParagrafo(() => ({ estilo: ESTILO_PADRAO, ajustes: null })))
          .run(),
    };
  },
});

/** O formato do parágrafo onde está o cursor. */
export function formatoNoCursor(editor: Editor): { estilo: string; modificado: boolean; formato: FormatoParagrafo } {
  const { $from } = editor.state.selection;
  for (let d = $from.depth; d >= 0; d--) {
    const node = $from.node(d);
    if (TIPOS.includes(node.type.name)) {
      const estilo = (node.attrs.estilo as string | null) ?? ESTILO_PADRAO;
      const ajustes = node.attrs.ajustes as Ajustes | null;
      return { estilo, modificado: !!ajustes && Object.keys(ajustes).length > 0, formato: formatoEfetivo(estilo, ajustes, node.attrs.estiloBase as Ajustes | null) };
    }
  }
  return { estilo: ESTILO_PADRAO, modificado: false, formato: formatoEfetivo(ESTILO_PADRAO, null) };
}
